import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { resetTokenVerificationCall, resetPasswordCall, isAuthenticated } from "../helper/apicalls/auth";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import edluminaHorizontalLogo from "../assets/png/edluminaHorizontalLogo.png";
import "../styles/Login.scss";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get("token");

  const [verifying, setVerifying] = useState(true);
  const [changeToken, setChangeToken] = useState(null);
  const [verificationError, setVerificationError] = useState("");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isAuthenticated()) {
      navigate("/dashboard");
      return;
    }

    const verifyToken = async () => {
      if (!resetToken) {
        setVerificationError("No reset token provided. Please request a new password reset link.");
        setVerifying(false);
        return;
      }
      try {
        const response = await resetTokenVerificationCall(resetToken);
        if (response.verified && response.change_password_token) {
          setChangeToken(response.change_password_token);
        } else {
          setVerificationError(response.message || "Token verification failed.");
        }
      } catch (err) {
        console.error("Token verification error:", err);
        setVerificationError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            "The password reset link is invalid or has expired."
        );
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [resetToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) return;

    if (newPassword.length < 6) {
      setSubmitError("Password must be at least 6 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setSubmitError("Passwords do not match.");
      return;
    }

    setSubmitError("");
    setSubmitLoading(true);

    try {
      await resetPasswordCall(changeToken, newPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err) {
      console.error("Reset password failed:", err);
      setSubmitError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to reset password. Please try again."
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="login-page">
      <img
        src={edluminaHorizontalLogo}
        className="navbar-logo-img"
        alt="Edlumina logo"
      />
      <div className="login-container-wrapper">
        <div className="login-container">
          {verifying ? (
            <div style={{ padding: "20px 0", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div className="ui-spinner" style={{
                border: "3px solid #f3f3f3",
                borderTop: "3px solid var(--accent-2, #0056b3)",
                borderRadius: "50%",
                width: "30px",
                height: "30px",
                animation: "spin 1s linear infinite",
                marginBottom: "20px"
              }}></div>
              <h2 style={{ fontSize: "16px", fontWeight: "600", color: "var(--text-secondary)", margin: 0 }}>
                Verifying reset link...
              </h2>
            </div>
          ) : verificationError ? (
            <div>
              <h1 className="logo-text" style={{ textAlign: "center", color: "#c33", fontSize: "24px", marginBottom: "16px" }}>
                Link Expired
              </h1>
              <p style={{ margin: "20px 0", fontSize: "14px", color: "var(--text-secondary)", lineHeight: "1.5" }}>
                {verificationError}
              </p>
              <button
                onClick={() => navigate("/login")}
                className="login-btn"
                style={{ width: "100%" }}
              >
                Go to Sign In
              </button>
            </div>
          ) : success ? (
            <div>
              <h1 className="logo-text" style={{ textAlign: "center", color: "#28a745", fontSize: "24px", marginBottom: "16px" }}>
                Success!
              </h1>
              <p style={{ margin: "20px 0", fontSize: "14px", color: "var(--text-secondary)", lineHeight: "1.5" }}>
                Your password has been reset successfully. Redirecting you to the Sign In page...
              </p>
            </div>
          ) : (
            <div>
              <div className="login-header" style={{ marginBottom: "24px" }}>
                <h1 className="logo-text" style={{ textAlign: "center", fontSize: "24px", marginBottom: "8px" }}>
                  Reset Password
                </h1>
                <p className="login-subtitle">Please enter your new password below.</p>
              </div>

              <form className="login-form" onSubmit={handleSubmit}>
                {submitError && <div className="error-message">{submitError}</div>}

                <div className="form-group password-group">
                  <label htmlFor="newPassword">New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="newPassword"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      disabled={submitLoading}
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={submitLoading}
                    >
                      {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </button>
                  </div>
                </div>

                <div className="form-group password-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={submitLoading}
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={submitLoading}
                    >
                      {showConfirmPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </button>
                  </div>
                </div>

                <button type="submit" className="login-btn" disabled={submitLoading}>
                  {submitLoading ? "Updating..." : "Reset Password"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
